import { useEffect, useMemo, useState, useCallback, useRef, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAccountant as canAccount, isManager as canManage } from "@/lib/roles";
import { computeShift, kes, ltr, STATUS_LABELS } from "@/lib/format";
import { StatusBadge, VarianceBadge } from "@/components/StatusBadge";
import AuditTrailDrawer from "@/components/AuditTrailDrawer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Send, Lock, Unlock, CheckCircle2, ClipboardCheck, History,
  FileSpreadsheet, FileText, Loader2, Fuel, Gauge, Package, Wallet, Receipt, TriangleAlert, ShieldAlert, ListChecks, Smartphone, Truck,
} from "lucide-react";

const LOCKED_EDIT = (status) => status !== "draft";

// Spreadsheet-style arrow-key navigation between data-entry cells, scoped to each <table>.
// Up/Down moves to the same column in the previous/next row; Left/Right (number fields only,
// so text-field cursor movement still works) moves to the adjacent cell in the same row.
function handleGridKeyDown(e) {
  const key = e.key;
  if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Enter"].includes(key)) return;
  const target = e.target;
  if (target.tagName !== "INPUT") return;
  if ((key === "ArrowLeft" || key === "ArrowRight") && target.type !== "number") return;

  const table = target.closest("table");
  if (!table) return;
  const rows = Array.from(table.querySelectorAll("tbody tr"))
    .map((tr) => Array.from(tr.querySelectorAll("input:not([disabled])")))
    .filter((cells) => cells.length > 0);

  let ri = -1, ci = -1;
  for (let r = 0; r < rows.length; r++) {
    const idx = rows[r].indexOf(target);
    if (idx !== -1) { ri = r; ci = idx; break; }
  }
  if (ri === -1) return;

  let dest = null;
  if (key === "ArrowDown" || key === "Enter") {
    if (ri + 1 < rows.length) dest = rows[ri + 1][Math.min(ci, rows[ri + 1].length - 1)];
  } else if (key === "ArrowUp") {
    if (ri - 1 >= 0) dest = rows[ri - 1][Math.min(ci, rows[ri - 1].length - 1)];
  } else if (key === "ArrowRight") {
    if (ci + 1 < rows[ri].length) dest = rows[ri][ci + 1];
  } else if (key === "ArrowLeft") {
    if (ci - 1 >= 0) dest = rows[ri][ci - 1];
  }
  if (dest) {
    e.preventDefault();
    dest.focus();
    dest.select?.();
  }
}

export default function ShiftEntry() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [shift, setShift] = useState(null);
  const [entry, setEntry] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjustments, setAdjustments] = useState([]);
  const [itemizeOpen, setItemizeOpen] = useState(false);
  const [transition, setTransition] = useState(null); // {action, title}
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    const [cfgRes, shiftRes] = await Promise.all([api.get("/config"), api.get(`/shifts/${id}`)]);
    setCfg(cfgRes.data);
    setShift(shiftRes.data);
    // build editable entry from shift or seed rows from config
    const s = shiftRes.data;
    const c = cfgRes.data;
    const activePumps = c.pumps.filter((p) => p.active !== false);
    const activeTanks = c.tanks.filter((t) => t.active !== false);
    const activeItems = c.nonfuel_items.filter((i) => i.active !== false);
    const activeMethods = c.payment_methods.filter((m) => m.active !== false);

    const fuelMap = Object.fromEntries((s.fuel_readings || []).map((r) => [String(r.pump_id), r]));
    const tankMap = Object.fromEntries((s.tank_dips || []).map((r) => [String(r.tank_id), r]));
    const itemMap = Object.fromEntries((s.nonfuel || []).map((r) => [String(r.item_id), r]));
    const payMap = Object.fromEntries((s.payments || []).map((r) => [`${r.attendant_id || ""}|${r.method_id}`, r]));
    const activeAttendants = c.attendants.filter((a) => a.active !== false);
    const payGroups = [...activeAttendants.map((a) => a.id), ""]; // "" = Unassigned / Shop
    const activeTills = (c.tills || []).filter((t) => t.active !== false);
    const tillMap = Object.fromEntries((s.till_ledger || []).map((r) => [String(r.till_id), r]));
    const nonfuelPayMap = Object.fromEntries((s.nonfuel_payments || []).map((r) => [`${r.group}|${r.method_id}`, r]));
    const NONFUEL_GROUPS = ["lube", "lpg_accessory"];

    // Prices are set in Station Setup, not editable per-shift. A draft takes the prices in force on
    // its own date (a price entered later never reaches an older shift, e.g. one reopened to
    // correct it); a cost price not yet entered by then is the first one entered after it (the
    // accountant often adds cost a day or two later). Once submitted/locked, the shift can no longer
    // be saved, so whatever was last computed stays frozen as history.
    const fuel_prices = { ...(s.fuel_prices || {}) };
    const fuel_cost_prices = { ...(s.fuel_cost_prices || {}) };
    if (!LOCKED_EDIT(s.status)) {
      try {
        const pr = await api.get("/config/fuel-prices");
        const at = (p) => new Date(p.effective_from || p.created_at).getTime();
        const dayEnd = new Date(`${s.date}T23:59:59+03:00`).getTime();
        const oldestFirst = [...pr.data].sort((a, b) => at(a) - at(b));
        const price = {};
        const cost = {};
        const costAfter = {};
        oldestFirst.forEach((p) => {
          if (at(p) <= dayEnd) {
            price[p.product_id] = p.unit_price;
            if (p.cost_price != null) cost[p.product_id] = p.cost_price;
          } else if (p.cost_price != null && !(p.product_id in costAfter)) {
            costAfter[p.product_id] = p.cost_price;
          }
        });
        Object.assign(fuel_prices, price);
        Object.assign(fuel_cost_prices, costAfter, cost);
      } catch (e) { console.debug("fuel price refresh skipped", e); }
    }

    setEntry({
      supervisor_name: s.supervisor_name || "",
      fuel_prices,
      fuel_cost_prices,
      pump_attendants: { ...(s.pump_attendants || {}) },
      till_attendants: { ...(s.till_attendants || {}) },
      nonfuel_group_attendants: { ...(s.nonfuel_group_attendants || {}) },
      nonfuel_payments: NONFUEL_GROUPS.flatMap((group) =>
        activeMethods.map((m) => {
          const key = `${group}|${m.id}`;
          return { group, method_id: m.id, amount: nonfuelPayMap[key]?.amount ?? "", reference: nonfuelPayMap[key]?.reference ?? "", till_id: nonfuelPayMap[key]?.till_id ?? "" };
        })
      ),
      till_ledger: activeTills.map((t) => ({
        till_id: t.id,
        opening_balance: tillMap[t.id]?.opening_balance ?? "", withdrawal: tillMap[t.id]?.withdrawal ?? "",
        actual_balance: tillMap[t.id]?.actual_balance ?? "",
        _openingLocked: hasValue(tillMap[t.id]?.opening_balance),
      })),
      fuel_readings: activePumps.map((p) => ({
        pump_id: p.id, product_id: p.product_id,
        opening: fuelMap[p.id]?.opening ?? "", closing: fuelMap[p.id]?.closing ?? "", testing: fuelMap[p.id]?.testing ?? "",
        elec_opening_l: fuelMap[p.id]?.elec_opening_l ?? "", elec_closing_l: fuelMap[p.id]?.elec_closing_l ?? "",
        elec_opening_kes: fuelMap[p.id]?.elec_opening_kes ?? "", elec_closing_kes: fuelMap[p.id]?.elec_closing_kes ?? "",
        _openingLocked: hasValue(fuelMap[p.id]?.opening),
      })),
      tank_dips: activeTanks.map((t) => ({
        tank_id: t.id, product_id: t.product_id,
        opening_dip: tankMap[t.id]?.opening_dip ?? "", deliveries: tankMap[t.id]?.deliveries ?? "",
        rtt: tankMap[t.id]?.rtt ?? "", closing_dip: tankMap[t.id]?.closing_dip ?? "",
        _openingLocked: hasValue(tankMap[t.id]?.opening_dip),
      })),
      nonfuel: activeItems.map((it) => ({
        item_id: it.id, unit_price: itemMap[it.id]?.unit_price ?? it.unit_price, cost_price: itemMap[it.id]?.cost_price ?? it.cost_price,
        opening: itemMap[it.id]?.opening ?? "", received: itemMap[it.id]?.received ?? "", closing: itemMap[it.id]?.closing ?? "",
        _openingLocked: hasValue(itemMap[it.id]?.opening),
      })),
      payments: payGroups.flatMap((attId) =>
        activeMethods.map((m) => {
          const key = `${attId}|${m.id}`;
          return { attendant_id: attId, method_id: m.id, amount: payMap[key]?.amount ?? "", reference: payMap[key]?.reference ?? "" };
        })
      ),
      expenses: (s.expenses || []).map((e) => ({ ...e, _k: e._k || uid() })),
      payment_transactions: (s.payment_transactions || []).map((t) => ({ ...t, _k: t._k || uid() })),
      account_receipts: (s.account_receipts || []).map((a) => ({ ...a, _k: a._k || uid() })),
      invoice_lines: (s.invoice_lines || []).map((l) => ({ ...l, _k: l._k || uid() })),
      tank_deliveries: (s.tank_deliveries || []).map((d) => ({ ...d, _k: d._k || uid() })),
      nonfuel_deliveries: (s.nonfuel_deliveries || []).map((d) => ({ ...d, _k: d._k || uid() })),
      notes: s.notes || "",
    });
    setDirty(false);
  }, [id]);

  useEffect(() => {
    load().catch((e) => {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      navigate("/");
    });
  }, [load, navigate]);

  const computed = useMemo(() => {
    if (!cfg || !entry) return null;
    return computeShift(
      {
        fuel_prices: entry.fuel_prices,
        fuel_cost_prices: entry.fuel_cost_prices,
        fuel_readings: entry.fuel_readings.map((r) => ({
          ...r, opening: num(r.opening), closing: num(r.closing), testing: num(r.testing),
          elec_opening_l: num(r.elec_opening_l), elec_closing_l: num(r.elec_closing_l),
          elec_opening_kes: num(r.elec_opening_kes), elec_closing_kes: num(r.elec_closing_kes),
        })),
        tank_dips: entry.tank_dips.map((r) => ({ ...r, opening_dip: num(r.opening_dip), deliveries: num(r.deliveries), rtt: num(r.rtt), closing_dip: num(r.closing_dip) })),
        tank_deliveries: entry.tank_deliveries.map((d) => ({
          ...d, invoice_amount: num(d.invoice_amount), invoice_volume: num(d.invoice_volume), offloaded_quantity: num(d.offloaded_quantity),
        })),
        nonfuel: entry.nonfuel.map((r) => ({ ...r, opening: num(r.opening), received: num(r.received), closing: num(r.closing), unit_price: num(r.unit_price), cost_price: num(r.cost_price) })),
        nonfuel_deliveries: entry.nonfuel_deliveries.map((d) => ({
          ...d, invoice_amount: num(d.invoice_amount), invoice_quantity: num(d.invoice_quantity), actual_quantity: num(d.actual_quantity),
        })),
        payments: entry.payments.map((r) => ({ ...r, amount: num(r.amount) })),
        payment_transactions: entry.payment_transactions.map((t) => ({ ...t, amount: num(t.amount) })),
        account_receipts: (entry.account_receipts || []).map((a) => ({ ...a, amount: num(a.amount) })),
        expenses: entry.expenses.map((e) => ({ ...e, amount: num(e.amount) })),
        pump_attendants: entry.pump_attendants,
        till_attendants: entry.till_attendants,
        till_ledger: entry.till_ledger.map((t) => ({ ...t, opening_balance: num(t.opening_balance), withdrawal: num(t.withdrawal), actual_balance: t.actual_balance === "" ? null : num(t.actual_balance) })),
        nonfuel_group_attendants: entry.nonfuel_group_attendants,
        nonfuel_payments: entry.nonfuel_payments.map((p) => ({ ...p, amount: num(p.amount), till_id: p.till_id || null })),
      },
      cfg
    );
  }, [cfg, entry]);

  // Autosave: without this, a page closed right after typing loses everything, since data only
  // lives in this component's state until "Save Draft" is clicked. Debounce persists ~1.5s after
  // the user stops typing; visibilitychange/beforeunload cover the tab-closed-immediately case.
  // Hooks must run unconditionally (before the loading guard below), so they read live values via
  // refs — saveRef is pointed at the real save() function further down, once it's defined.
  const saveRef = useRef(() => {});
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  useEffect(() => {
    if (!dirty || !shift || LOCKED_EDIT(shift.status)) return;
    const timer = setTimeout(() => { saveRef.current({ silent: true }); }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, dirty, shift]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden" && dirtyRef.current) saveRef.current({ silent: true });
    };
    const onBeforeUnload = (e) => {
      if (dirtyRef.current) { e.preventDefault(); e.returnValue = ""; }
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);

  if (!cfg || !entry || !shift || !computed) {
    return <div className="flex py-20 justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const readOnly = LOCKED_EDIT(shift.status);
  const isManager = canManage(user);
  const isAccountant = canAccount(user);

  const set = (path) => (val) => {
    setEntry((prev) => {
      const next = structuredClone(prev);
      path(next, val);
      return next;
    });
    setDirty(true);
  };

  const buildPayload = () => ({
    supervisor_name: entry.supervisor_name,
    fuel_prices: Object.fromEntries(Object.entries(entry.fuel_prices).map(([k, v]) => [k, num(v)])),
    fuel_cost_prices: Object.fromEntries(Object.entries(entry.fuel_cost_prices || {}).map(([k, v]) => [k, num(v)])),
    pump_attendants: entry.pump_attendants || {},
    till_attendants: entry.till_attendants || {},
    till_ledger: entry.till_ledger.map((t) => ({ till_id: t.till_id, opening_balance: num(t.opening_balance), withdrawal: num(t.withdrawal), actual_balance: t.actual_balance === "" ? null : num(t.actual_balance) })),
    nonfuel_group_attendants: entry.nonfuel_group_attendants || {},
    nonfuel_payments: entry.nonfuel_payments.map((p) => ({ group: p.group, method_id: p.method_id, amount: num(p.amount), reference: p.reference || "", till_id: p.till_id || null })),
    fuel_readings: entry.fuel_readings.map((r) => ({
      pump_id: r.pump_id, product_id: r.product_id, opening: num(r.opening), closing: num(r.closing), testing: num(r.testing),
      elec_opening_l: num(r.elec_opening_l), elec_closing_l: num(r.elec_closing_l),
      elec_opening_kes: num(r.elec_opening_kes), elec_closing_kes: num(r.elec_closing_kes),
    })),
    tank_dips: entry.tank_dips.map((r) => ({ tank_id: r.tank_id, product_id: r.product_id, opening_dip: num(r.opening_dip), deliveries: num(r.deliveries), rtt: num(r.rtt), closing_dip: num(r.closing_dip) })),
    tank_deliveries: entry.tank_deliveries.map((d) => ({
      _k: d._k, tank_id: d.tank_id, invoice_no: d.invoice_no || "", date: d.date || "",
      product_id: d.product_id || null, invoice_amount: num(d.invoice_amount),
      invoice_volume: num(d.invoice_volume), offloaded_quantity: num(d.offloaded_quantity),
    })),
    nonfuel: entry.nonfuel.map((r) => ({ item_id: r.item_id, opening: num(r.opening), received: num(r.received), closing: num(r.closing), unit_price: num(r.unit_price), cost_price: num(r.cost_price) })),
    nonfuel_deliveries: entry.nonfuel_deliveries.map((d) => ({
      _k: d._k, item_id: d.item_id, invoice_no: d.invoice_no || "", date: d.date || "",
      invoice_amount: num(d.invoice_amount), invoice_quantity: num(d.invoice_quantity), actual_quantity: num(d.actual_quantity),
    })),
    account_receipts: (entry.account_receipts || []).map((a) => ({
      _k: a._k, account_id: a.account_id || null, method_id: a.method_id || null, till_id: a.till_id || null,
      amount: num(a.amount), reference: a.reference || "", note: a.note || "", bank: a.bank || null,
    })),
    payments: entry.payments.map((r) => ({ attendant_id: r.attendant_id || null, method_id: r.method_id, amount: num(r.amount), reference: r.reference })),
    payment_transactions: entry.payment_transactions.map((t) => ({ attendant_id: t.attendant_id || null, method_id: t.method_id, amount: num(t.amount), reference: t.reference || "", note: t.note || "",
      ...(t.auth_code ? { auth_code: String(t.auth_code).trim().toUpperCase() } : {}) })),
    expenses: entry.expenses.map((e) => ({ ...e, amount: num(e.amount) })),
    invoice_lines: entry.invoice_lines.map((l) => ({
      _k: l._k, attendant_id: l.attendant_id || null, method_id: l.method_id || null, customer_id: l.customer_id || null,
      invoice_no: l.invoice_no || "", vehicle_reg: l.vehicle_reg || "", product_id: l.product_id || null,
      litres: num(l.litres), amount: num(l.amount),
    })),
    notes: entry.notes,
  });

  const save = async ({ silent = false } = {}) => {
    if (!silent) setSaving(true);
    try {
      const { data } = await api.put(`/shifts/${id}`, buildPayload());
      setShift(data);
      setDirty(false);
      if (!silent) toast.success("Draft saved");
    } catch (e) {
      if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      if (!silent) setSaving(false);
    }
  };

  saveRef.current = save;

  const runTransition = async () => {
    setBusy(true);
    try {
      const endpoints = {
        submit: `/shifts/${id}/submit`,
        "manager-review": `/shifts/${id}/manager-review`,
        "accountant-approve": `/shifts/${id}/accountant-approve`,
        lock: `/shifts/${id}/lock`,
        reopen: `/shifts/${id}/reopen`,
      };
      const body = transition.action === "reopen" ? { reason: note } : { note };
      if (transition.action === "submit" && dirty) await api.put(`/shifts/${id}`, buildPayload());
      const { data } = await api.post(endpoints[transition.action], body);
      setShift(data);
      await load();
      setTransition(null);
      setNote("");
      toast.success(`Shift ${transition.action.replace("-", " ")} done`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.validation_errors) {
        toast.error("Submission blocked: " + detail.validation_errors.join(" "));
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setBusy(false);
    }
  };

  const openAudit = async () => {
    const { data } = await api.get(`/shifts/${id}/audit`);
    setLogs(data);
    setAuditOpen(true);
  };

  const openAdjustments = async () => {
    const { data } = await api.get(`/shifts/${id}/adjustments`);
    setAdjustments(data);
    setAdjOpen(true);
  };

  const download = async (kind) => {
    try {
      const res = await api.get(`/reports/shift/${id}/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `shift_${shift.date}_${shift.shift_type}.${kind === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const t = computed.totals;
  const pumpById = Object.fromEntries(cfg.pumps.map((p) => [p.id, p]));
  const tankById = Object.fromEntries(cfg.tanks.map((tk) => [tk.id, tk]));
  const itemById = Object.fromEntries(cfg.nonfuel_items.map((i) => [i.id, i]));
  const methodById = Object.fromEntries(cfg.payment_methods.map((m) => [m.id, m]));
  const tillById = Object.fromEntries((cfg.tills || []).map((t) => [t.id, t]));
  const productById = Object.fromEntries(cfg.products.map((p) => [p.id, p]));
  const attendants = (cfg.attendants || []).filter((a) => a.active !== false);

  // Section D shows a card only for attendants actually assigned to a pump this shift (Section A),
  // plus Unassigned/Shop once it has data — it doesn't list every configured attendant up front.
  const assignedAttendantIds = new Set(Object.values(entry.pump_attendants || {}).filter(Boolean));
  entry.payments.forEach((p) => { if (p.attendant_id && num(p.amount)) assignedAttendantIds.add(p.attendant_id); });
  const paymentGroups = [...attendants.filter((a) => assignedAttendantIds.has(a.id)), { id: "", name: "Unassigned / Shop" }];

  // group pumps by island, then order rows so all pumps of one product come before the next
  // product (e.g. Super, Super2, Diesel, Diesel2) rather than however they were created
  const productOrder = Object.fromEntries(cfg.products.map((p, i) => [p.id, i]));
  const islands = {};
  entry.fuel_readings.forEach((r, idx) => {
    const isl = pumpById[r.pump_id]?.island || "—";
    (islands[isl] = islands[isl] || []).push({ ...r, idx });
  });
  Object.values(islands).forEach((rows) => rows.sort((a, b) => (productOrder[a.product_id] ?? 0) - (productOrder[b.product_id] ?? 0)));

  return (
    <div className="space-y-6 pb-28" onKeyDown={handleGridKeyDown}>
      {/* Sticky status banner */}
      <div className="sticky top-16 z-30 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-brand px-5 py-3 text-white shadow-md">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="text-slate-200 hover:bg-white/10 hover:text-white" data-testid="back-to-shifts">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="font-display text-lg font-bold leading-tight">
              {shift.date} · {shift.shift_type} Shift
            </div>
            <div className="text-xs text-slate-300">Supervisor: {entry.supervisor_name || "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {readOnly && <span className="flex items-center gap-1 text-xs text-amber-300"><Lock className="h-3.5 w-3.5" /> Direct edit disabled</span>}
          <StatusBadge status={shift.status} />
        </div>
      </div>

      {/* Variance summary (Section F) */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <SummaryStat label="Fuel Sales" value={`KSh ${kes(t.fuel_sales_total)}`} icon={Fuel} />
        <SummaryStat label="Non-Fuel Rev" value={`KSh ${kes(t.nonfuel_revenue_total)}`} icon={Package} />
        <SummaryStat label="Total Sales" value={`KSh ${kes(t.total_sales)}`} icon={Wallet} />
        <SummaryStat label="Expected Cash" value={`KSh ${kes(t.expected_cash)}`} icon={Receipt} />
        <SummaryStat label="Actual Cash" value={`KSh ${kes(t.actual_cash)}`} icon={Wallet} />
        <SummaryStat
          label="Cash Variance"
          value={`${t.cash_variance >= 0 ? "+" : ""}${kes(t.cash_variance)}`}
          icon={t.cash_status === "within" ? CheckCircle2 : TriangleAlert}
          status={t.cash_status}
        />
      </div>

      <Tabs defaultValue="pumps">
        <TabsList>
          <TabsTrigger value="pumps" data-testid="shift-tab-pumps"><Fuel className="mr-1.5 h-4 w-4" />Pumps</TabsTrigger>
          <TabsTrigger value="shop" data-testid="shift-tab-shop"><Package className="mr-1.5 h-4 w-4" />Lubes &amp; LPG</TabsTrigger>
          <TabsTrigger value="summary" data-testid="shift-tab-summary"><Wallet className="mr-1.5 h-4 w-4" />Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="pumps" className="mt-4 space-y-6">
      {/* Section A: Fuel pump readings */}
      <Section title="A · Fuel Pump Meter Readings" icon={Fuel}>
        <div className="mb-3 flex flex-wrap items-center gap-4 rounded-lg bg-slate-50 p-3">
          {cfg.products.map((p) => (
            <div key={p.id} className="flex items-center gap-2">
              <Label className="text-xs">{p.name} Price/L (KES)</Label>
              <span className="calc-cell inline-flex h-8 w-28 items-center justify-end rounded" data-testid={`fuel-price-${p.id}`}>
                {kes(entry.fuel_prices[p.id] || 0)}
              </span>
            </div>
          ))}
          <span className="text-xs text-muted-foreground">Set in Station Setup → Fuel Prices, not editable here.</span>
        </div>
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead>
              <tr>
                <th className="text-left" rowSpan={2}>Island / Pump</th>
                <th className="text-left" rowSpan={2}>Product</th>
                <th className="text-left" rowSpan={2}>Attendant</th>
                <th colSpan={5} className="bg-slate-50">Manual Reading</th>
                <th colSpan={6} className="bg-blue-50">Electronic Reading (authoritative)</th>
                <th colSpan={2} className="bg-amber-50">Variance</th>
              </tr>
              <tr>
                <th className="bg-slate-50">Open (L)</th><th className="bg-slate-50">Close (L)</th><th className="bg-slate-50">Testing (L)</th>
                <th className="bg-slate-50">Ltrs Sold</th><th className="bg-slate-50">Amount</th>
                <th className="bg-blue-50">Open (L)</th><th className="bg-blue-50">Close (L)</th><th className="bg-blue-50">Ltrs Sold</th>
                <th className="bg-blue-50">Open (KES)</th><th className="bg-blue-50">Close (KES)</th><th className="bg-blue-50">Amount</th>
                <th className="bg-amber-50">Ltrs</th><th className="bg-amber-50">KES</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(islands).map(([isl, rows]) => (
                <Fragment key={isl}>
                  <tr><td colSpan={16} className="bg-slate-100 !text-left text-xs font-bold uppercase tracking-wider text-slate-600">{isl}</td></tr>
                  {rows.map((r) => {
                    const line = computed.fuel_lines.find((l) => l.pump_id === r.pump_id) || {};
                    return (
                      <tr key={r.pump_id}>
                        <td className="!text-left font-medium">{pumpById[r.pump_id]?.label}</td>
                        <td className="!text-left text-muted-foreground">{productById[r.product_id]?.name}</td>
                        <td className="!text-left">
                          <select
                            className="grid-input !text-left"
                            style={{ textAlign: "left" }}
                            disabled={readOnly}
                            value={entry.pump_attendants?.[r.pump_id] || ""}
                            data-testid={`pump-attendant-${r.pump_id}`}
                            onChange={(e) => set((n, v) => { n.pump_attendants = { ...(n.pump_attendants || {}) }; n.pump_attendants[r.pump_id] = v; })(e.target.value)}
                          >
                            <option value="">— assign —</option>
                            {attendants.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                          </select>
                        </td>
                        <td><NumCell value={r.opening} disabled={readOnly || r._openingLocked} tid={`pump-opening-${r.pump_id}`} onChange={set((n, v) => (n.fuel_readings[r.idx].opening = v))} /></td>
                        <td><NumCell value={r.closing} disabled={readOnly} tid={`pump-closing-${r.pump_id}`} onChange={set((n, v) => (n.fuel_readings[r.idx].closing = v))} /></td>
                        <td><NumCell value={r.testing} disabled={readOnly} tid={`pump-testing-${r.pump_id}`} onChange={set((n, v) => (n.fuel_readings[r.idx].testing = v))} /></td>
                        <td className="calc-cell">{ltr(line.manual_liters_sold)}</td>
                        <td className="calc-cell">{kes(line.manual_amount)}</td>
                        <td><NumCell value={r.elec_opening_l} disabled={readOnly || r._openingLocked} tid={`pump-elec-opening-l-${r.pump_id}`} onChange={set((n, v) => (n.fuel_readings[r.idx].elec_opening_l = v))} /></td>
                        <td><NumCell value={r.elec_closing_l} disabled={readOnly} tid={`pump-elec-closing-l-${r.pump_id}`} onChange={set((n, v) => (n.fuel_readings[r.idx].elec_closing_l = v))} /></td>
                        <td className="calc-cell">{ltr(line.electronic_liters_sold)}</td>
                        <td><NumCell value={r.elec_opening_kes} disabled={readOnly || r._openingLocked} tid={`pump-elec-opening-kes-${r.pump_id}`} onChange={set((n, v) => (n.fuel_readings[r.idx].elec_opening_kes = v))} /></td>
                        <td><NumCell value={r.elec_closing_kes} disabled={readOnly} tid={`pump-elec-closing-kes-${r.pump_id}`} onChange={set((n, v) => (n.fuel_readings[r.idx].elec_closing_kes = v))} /></td>
                        <td className="calc-cell font-semibold">{kes(line.electronic_amount)}</td>
                        <td className={`calc-cell ${Math.abs(line.variance_liters || 0) > 1 ? "text-rose-600" : ""}`}>{ltr(line.variance_liters)}</td>
                        <td className={`calc-cell ${Math.abs(line.variance_amount || 0) > 100 ? "text-rose-600" : ""}`}>{kes(line.variance_amount)}</td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
              <tr className="font-semibold">
                <td className="!text-left" colSpan={7}>Total Fuel Sales</td>
                <td className="calc-cell">{kes(computed.fuel_lines.reduce((a, l) => a + (l.manual_amount || 0), 0))}</td>
                <td className="calc-cell" colSpan={2}>—</td>
                <td className="calc-cell">{ltr(computed.fuel_lines.reduce((a, l) => a + (l.electronic_liters_sold || 0), 0))}</td>
                <td className="calc-cell" colSpan={2}>—</td>
                <td className="calc-cell">{kes(t.fuel_sales_total)}</td>
                <td className="calc-cell">{ltr(computed.fuel_lines.reduce((a, l) => a + (l.variance_liters || 0), 0))}</td>
                <td className="calc-cell">{kes(computed.fuel_lines.reduce((a, l) => a + (l.variance_amount || 0), 0))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* Section B: Tank dips */}
      <Section title="B · Tank Dip & Reconciliation" icon={Gauge}>
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead>
              <tr>
                <th className="text-left">Tank</th>
                <th>Opening Dip</th><th>Deliveries</th><th>RTT</th><th>Pump Test</th><th>Closing Dip</th>
                <th>Dip Sales (L)</th><th>Pump Sales (L)</th><th>Variance (L)</th><th>Var %</th><th className="text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {entry.tank_dips.map((r, idx) => {
                const line = computed.tank_lines.find((l) => l.tank_id === r.tank_id) || {};
                const hasItemizedDeliveries = entry.tank_deliveries.some((d) => d.tank_id === r.tank_id);
                return (
                  <tr key={r.tank_id}>
                    <td className="!text-left font-medium">{tankById[r.tank_id]?.label}</td>
                    <td><NumCell value={r.opening_dip} disabled={readOnly || r._openingLocked} tid={`tank-opening-${r.tank_id}`} onChange={set((n, v) => (n.tank_dips[idx].opening_dip = v))} /></td>
                    <td>
                      {hasItemizedDeliveries ? (
                        <span className="calc-cell" title="From itemized deliveries below">{ltr(line.deliveries)}</span>
                      ) : (
                        <NumCell value={r.deliveries} disabled={readOnly} tid={`tank-deliveries-${r.tank_id}`} onChange={set((n, v) => (n.tank_dips[idx].deliveries = v))} />
                      )}
                    </td>
                    <td><NumCell value={r.rtt} disabled={readOnly} tid={`tank-rtt-${r.tank_id}`} onChange={set((n, v) => (n.tank_dips[idx].rtt = v))} /></td>
                    <td className="calc-cell text-muted-foreground" title="From pump testing on Section A — already back in the tank by closing dip, shown for visibility only">{ltr(line.pump_test)}</td>
                    <td><NumCell value={r.closing_dip} disabled={readOnly} tid={`tank-closing-${r.tank_id}`} onChange={set((n, v) => (n.tank_dips[idx].closing_dip = v))} /></td>
                    <td className="calc-cell">{ltr(line.dip_sales)}</td>
                    <td className="calc-cell">{ltr(line.pump_throughput)}</td>
                    <td className="calc-cell">{ltr(line.variance_l)}</td>
                    <td className="calc-cell">{ltr(line.variance_pct)}%</td>
                    <td className="!text-left"><VarianceBadge status={line.status} testId={`tank-variance-${r.tank_id}`}>{line.status}</VarianceBadge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Section B2: Fuel deliveries (itemized, feeds Tank Dip "Deliveries") */}
      <Section title="Fuel Deliveries" icon={Truck}>
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead>
              <tr>
                <th className="text-left">Tank</th>
                <th className="text-left">Invoice No.</th>
                <th className="text-left">Date</th>
                <th className="text-left">Product</th>
                <th>Invoice Amount (KES)</th>
                <th>Invoice Volume (L)</th>
                <th>Offloaded Qty (L)</th>
                <th>Variance (L)</th>
                <th>Var %</th>
                <th className="text-left">Status</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {entry.tank_deliveries.map((d, idx) => {
                const line = computed.tank_delivery_lines?.[idx] || {};
                return (
                  <tr key={d._k}>
                    <td className="!text-left">
                      <select
                        className="grid-input !text-left"
                        style={{ textAlign: "left" }}
                        disabled={readOnly}
                        value={d.tank_id || ""}
                        data-testid={`delivery-tank-${idx}`}
                        onChange={(e) => set((n, v) => {
                          n.tank_deliveries[idx].tank_id = v;
                          n.tank_deliveries[idx].product_id = tankById[v]?.product_id || "";
                        })(e.target.value)}
                      >
                        <option value="">— select tank —</option>
                        {cfg.tanks.map((tk) => <option key={tk.id} value={tk.id}>{tk.label}</option>)}
                      </select>
                    </td>
                    <td className="!text-left">
                      <input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={d.invoice_no || ""} data-testid={`delivery-invoice-${idx}`} onChange={(e) => set((n, v) => (n.tank_deliveries[idx].invoice_no = v))(e.target.value)} />
                    </td>
                    <td className="!text-left">
                      <input type="date" className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={d.date || ""} data-testid={`delivery-date-${idx}`} onChange={(e) => set((n, v) => (n.tank_deliveries[idx].date = v))(e.target.value)} />
                    </td>
                    <td className="!text-left text-muted-foreground">{productById[d.product_id]?.name || productById[tankById[d.tank_id]?.product_id]?.name || "—"}</td>
                    <td><NumCell value={d.invoice_amount} disabled={readOnly} tid={`delivery-amount-${idx}`} onChange={set((n, v) => (n.tank_deliveries[idx].invoice_amount = v))} /></td>
                    <td><NumCell value={d.invoice_volume} disabled={readOnly} tid={`delivery-invoice-vol-${idx}`} onChange={set((n, v) => (n.tank_deliveries[idx].invoice_volume = v))} /></td>
                    <td><NumCell value={d.offloaded_quantity} disabled={readOnly} tid={`delivery-offloaded-${idx}`} onChange={set((n, v) => (n.tank_deliveries[idx].offloaded_quantity = v))} /></td>
                    <td className={`calc-cell ${Math.abs(line.variance || 0) > 1 ? "text-rose-600" : ""}`}>{ltr(line.variance)}</td>
                    <td className="calc-cell">{ltr(line.variance_pct)}%</td>
                    <td className="!text-left"><VarianceBadge status={line.status} testId={`delivery-status-${idx}`}>{line.status}</VarianceBadge></td>
                    <td className="text-center">{!readOnly && <button onClick={() => set((n) => n.tank_deliveries.splice(idx, 1))(null)} className="text-rose-500" data-testid={`delivery-remove-${idx}`}>×</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!readOnly && (
          <Button
            variant="outline" size="sm" className="mt-3"
            onClick={() => set((n) => n.tank_deliveries.push({ tank_id: "", invoice_no: "", date: "", product_id: "", invoice_amount: "", invoice_volume: "", offloaded_quantity: "", _k: uid() }))(null)}
            data-testid="add-delivery-button"
          >
            + Add Delivery
          </Button>
        )}
      </Section>
        </TabsContent>

        <TabsContent value="shop" className="mt-4 space-y-6">
      {/* Section C: Non-fuel */}
      <Section title="C · Non-Fuel Stock (Lubes, LPG, Accessories)" icon={Package}>
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead>
              <tr>
                <th className="text-left">Item</th>
                <th>Opening</th><th>Received</th><th>Closing</th><th>Sold</th>
                <th>Unit Price</th><th>Revenue</th>
                {isAccountant && <><th>Cost Price</th><th>Gross Profit</th></>}
              </tr>
            </thead>
            <tbody>
              {entry.nonfuel.map((r, idx) => {
                const line = computed.nonfuel_lines.find((l) => l.item_id === r.item_id) || {};
                const item = itemById[r.item_id];
                const hasItemizedDeliveries = entry.nonfuel_deliveries.some((d) => d.item_id === r.item_id);
                return (
                  <tr key={r.item_id}>
                    <td className="!text-left font-medium">{item?.name} <span className="text-xs text-muted-foreground">({item?.category})</span></td>
                    <td><NumCell value={r.opening} disabled={readOnly || r._openingLocked} tid={`nf-opening-${r.item_id}`} onChange={set((n, v) => (n.nonfuel[idx].opening = v))} /></td>
                    <td>
                      {hasItemizedDeliveries ? (
                        <span className="calc-cell" title="From itemized deliveries below">{ltr(line.received, 0)}</span>
                      ) : (
                        <NumCell value={r.received} disabled={readOnly} tid={`nf-received-${r.item_id}`} onChange={set((n, v) => (n.nonfuel[idx].received = v))} />
                      )}
                    </td>
                    <td><NumCell value={r.closing} disabled={readOnly} tid={`nf-closing-${r.item_id}`} onChange={set((n, v) => (n.nonfuel[idx].closing = v))} /></td>
                    <td className="calc-cell">{ltr(line.sold, 0)}</td>
                    <td><NumCell value={r.unit_price} disabled={readOnly} tid={`nf-price-${r.item_id}`} onChange={set((n, v) => (n.nonfuel[idx].unit_price = v))} /></td>
                    <td className="calc-cell">{kes(line.revenue)}</td>
                    {isAccountant && <>
                      <td className="calc-cell">{kes(line.cost_price)}</td>
                      <td className="calc-cell">{kes(line.gross_profit)}</td>
                    </>}
                  </tr>
                );
              })}
              <tr className="font-semibold">
                <td className="!text-left" colSpan={6}>Non-Fuel Totals</td>
                <td className="calc-cell">{kes(t.nonfuel_revenue_total)}</td>
                {isAccountant && <td className="calc-cell" colSpan={2}>{kes(t.nonfuel_gross_total)}</td>}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* Dry Stock Deliveries (itemized: invoice details + received vs invoiced quantity, feeds Section C "Received") */}
      <Section title="Dry Stock Deliveries" icon={Truck}>
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead>
              <tr>
                <th className="text-left">Item</th>
                <th className="text-left">Invoice No.</th>
                <th className="text-left">Date</th>
                <th>Invoice Amount (KES)</th>
                <th>Invoice Qty</th>
                <th>Actual Qty</th>
                <th>Variance</th>
                <th>Var %</th>
                <th className="text-left">Status</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {entry.nonfuel_deliveries.map((d, idx) => {
                const line = computed.nonfuel_delivery_lines?.[idx] || {};
                return (
                  <tr key={d._k}>
                    <td className="!text-left">
                      <select
                        className="grid-input !text-left"
                        style={{ textAlign: "left" }}
                        disabled={readOnly}
                        value={d.item_id || ""}
                        data-testid={`nf-delivery-item-${idx}`}
                        onChange={(e) => set((n, v) => (n.nonfuel_deliveries[idx].item_id = v))(e.target.value)}
                      >
                        <option value="">— select item —</option>
                        {cfg.nonfuel_items.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                      </select>
                    </td>
                    <td className="!text-left">
                      <input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={d.invoice_no || ""} data-testid={`nf-delivery-invoice-${idx}`} onChange={(e) => set((n, v) => (n.nonfuel_deliveries[idx].invoice_no = v))(e.target.value)} />
                    </td>
                    <td className="!text-left">
                      <input type="date" className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={d.date || ""} data-testid={`nf-delivery-date-${idx}`} onChange={(e) => set((n, v) => (n.nonfuel_deliveries[idx].date = v))(e.target.value)} />
                    </td>
                    <td><NumCell value={d.invoice_amount} disabled={readOnly} tid={`nf-delivery-amount-${idx}`} onChange={set((n, v) => (n.nonfuel_deliveries[idx].invoice_amount = v))} /></td>
                    <td><NumCell value={d.invoice_quantity} disabled={readOnly} tid={`nf-delivery-invoice-qty-${idx}`} onChange={set((n, v) => (n.nonfuel_deliveries[idx].invoice_quantity = v))} /></td>
                    <td><NumCell value={d.actual_quantity} disabled={readOnly} tid={`nf-delivery-actual-qty-${idx}`} onChange={set((n, v) => (n.nonfuel_deliveries[idx].actual_quantity = v))} /></td>
                    <td className={`calc-cell ${Math.abs(line.variance || 0) > 0 ? "text-rose-600" : ""}`}>{ltr(line.variance, 0)}</td>
                    <td className="calc-cell">{ltr(line.variance_pct)}%</td>
                    <td className="!text-left"><VarianceBadge status={line.status} testId={`nf-delivery-status-${idx}`}>{line.status}</VarianceBadge></td>
                    <td className="text-center">{!readOnly && <button onClick={() => set((n) => n.nonfuel_deliveries.splice(idx, 1))(null)} className="text-rose-500" data-testid={`nf-delivery-remove-${idx}`}>×</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!readOnly && (
          <Button
            variant="outline" size="sm" className="mt-3"
            onClick={() => set((n) => n.nonfuel_deliveries.push({ item_id: "", invoice_no: "", date: "", invoice_amount: "", invoice_quantity: "", actual_quantity: "", _k: uid() }))(null)}
            data-testid="add-nf-delivery-button"
          >
            + Add Delivery
          </Button>
        )}
      </Section>

      {/* Non-fuel declaration: the shop attendant declares what they collected for Lubes and for
          LPG & Accessories separately, by payment method (not all cash) — checked against revenue
          calculated from stock movement in Section C above. M-Pesa declared here combines with
          fuel M-Pesa (Section D) for that attendant's till total in Section F. */}
      <Section title="Non-Fuel Declaration (Lubes / LPG & Accessories)" icon={Package}>
        <div className="space-y-4">
          {[{ key: "lube", label: "Lubes" }, { key: "lpg_accessory", label: "LPG & Accessories" }].map((g) => {
            const line = computed.nonfuel_group_lines.find((l) => l.group === g.key) || {};
            const rows = entry.nonfuel_payments.map((p, idx) => ({ ...p, idx })).filter((p) => p.group === g.key);
            return (
              <Card key={g.key} className="overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-slate-50 px-4 py-2.5">
                  <div className="flex items-center gap-3">
                    <h4 className="text-sm font-semibold text-slate-800">{g.label}</h4>
                    <select
                      className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly}
                      value={entry.nonfuel_group_attendants?.[g.key] || ""}
                      data-testid={`nonfuel-attendant-${g.key}`}
                      onChange={(e) => set((n, v) => { n.nonfuel_group_attendants = { ...(n.nonfuel_group_attendants || {}) }; n.nonfuel_group_attendants[g.key] = v; })(e.target.value)}
                    >
                      <option value="">— assign —</option>
                      {attendants.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                    <span>Expected Revenue: <span className="font-mono-fin font-semibold text-slate-700">KSh {kes(line.expected_revenue)}</span></span>
                    <span>Declared: <span className="font-mono-fin font-semibold text-slate-700">KSh {kes(line.declared_amount)}</span></span>
                    <span className={`font-mono-fin font-semibold ${Math.abs(line.variance || 0) <= 100 ? "text-emerald-600" : "text-rose-600"}`}>
                      Variance: {line.variance >= 0 ? "+" : ""}{kes(line.variance)}
                    </span>
                    {line.status && <VarianceBadge status={line.status}>{line.status}</VarianceBadge>}
                  </div>
                </div>
                <div className="overflow-x-auto p-3">
                  <table className="recon-table">
                    <thead><tr><th className="text-left">Payment Method</th><th className="text-left">Type</th><th>Amount (KES)</th><th className="text-left">Reference</th><th className="text-left">Till (M-Pesa)</th></tr></thead>
                    <tbody>
                      {rows.map((p) => (
                        <tr key={p.method_id}>
                          <td className="!text-left font-medium">{methodById[p.method_id]?.name}</td>
                          <td className="!text-left text-xs text-muted-foreground">{methodById[p.method_id]?.kind}</td>
                          <td><NumCell value={p.amount} disabled={readOnly} tid={`nonfuel-pay-amount-${g.key}-${p.method_id}`} onChange={set((n, v) => (n.nonfuel_payments[p.idx].amount = v))} /></td>
                          <td><input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={p.reference} data-testid={`nonfuel-pay-ref-${g.key}-${p.method_id}`} onChange={(e) => set((n, v) => (n.nonfuel_payments[p.idx].reference = v))(e.target.value)} /></td>
                          <td className="!text-left">
                            {methodById[p.method_id]?.kind === "mobile_money" ? (
                              <select
                                className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly}
                                value={p.till_id || ""} data-testid={`nonfuel-pay-till-${g.key}-${p.method_id}`}
                                onChange={(e) => set((n, v) => (n.nonfuel_payments[p.idx].till_id = v))(e.target.value)}
                              >
                                <option value="">— which till? —</option>
                                {(cfg.tills || []).filter((tl) => tl.active !== false).map((tl) => <option key={tl.id} value={tl.id}>{tl.label}</option>)}
                              </select>
                            ) : <span className="text-xs text-muted-foreground">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            );
          })}
        </div>
      </Section>
        </TabsContent>

        <TabsContent value="summary" className="mt-4 space-y-6">
      {/* Section D: Payments, entered per attendant — only shows attendants actually assigned to a
          pump this shift (Section A), plus Unassigned/Shop once it has data */}
      <Section title="D · Attendant Payment Collections" icon={Wallet}>
        <div className="space-y-4">
          {assignedAttendantIds.size === 0 && !entry.payments.some((p) => !p.attendant_id && num(p.amount)) && (
            <p className="text-sm text-muted-foreground" data-testid="payments-empty-hint">
              Assign attendants to pumps in Section A to enter their payment collections here.
            </p>
          )}
          {paymentGroups.map((att) => {
            const rows = entry.payments.map((r, idx) => ({ ...r, idx })).filter((r) => (r.attendant_id || "") === att.id);
            const line = computed.attendant_lines.find((l) => (l.attendant_id || "") === (att.id || "")) || { assigned_fuel_sales: 0, collected_total: 0, variance: 0 };
            if (att.id === "" && rows.every((r) => !num(r.amount)) && !line.assigned_fuel_sales) return null;
            return (
              <Card key={att.id || "unassigned"} className="overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-slate-50 px-4 py-2.5">
                  <h4 className="text-sm font-semibold text-slate-800">{att.name}</h4>
                  <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                    <span>Assigned Fuel Sales: <span className="font-mono-fin font-semibold text-slate-700">KSh {kes(line.assigned_fuel_sales)}</span></span>
                    <span>Collected: <span className="font-mono-fin font-semibold text-slate-700">KSh {kes(line.collected_total)}</span></span>
                    <span className={`font-mono-fin font-semibold ${Math.abs(line.variance || 0) <= 100 ? "text-emerald-600" : "text-rose-600"}`}>
                      Variance: {line.variance >= 0 ? "+" : ""}{kes(line.variance)}
                    </span>
                    {line.status && <VarianceBadge status={line.status}>{line.status}</VarianceBadge>}
                  </div>
                </div>
                <div className="overflow-x-auto p-3">
                  <table className="recon-table">
                    <thead><tr><th className="text-left">Payment Method</th><th className="text-left">Type</th><th>Amount (KES)</th><th className="text-left">Reference</th></tr></thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.method_id}>
                          <td className="!text-left font-medium">{methodById[r.method_id]?.name}</td>
                          <td className="!text-left text-xs text-muted-foreground">{methodById[r.method_id]?.kind}</td>
                          <td><NumCell value={r.amount} disabled={readOnly} tid={`pay-amount-${att.id || "unassigned"}-${r.method_id}`} onChange={set((n, v) => (n.payments[r.idx].amount = v))} /></td>
                          <td><input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={r.reference} data-testid={`pay-ref-${att.id || "unassigned"}-${r.method_id}`} onChange={(e) => set((n, v) => (n.payments[r.idx].reference = v))(e.target.value)} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            );
          })}
        </div>
      </Section>

      {/* Section D2: Shift-wide payment totals + cash reconciliation (derived from attendant entries above) */}
      <Section title="Payment Totals & Cash Reconciliation" icon={Receipt}>
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="overflow-x-auto">
            <table className="recon-table">
              <thead><tr><th className="text-left">Payment Method</th><th className="text-left">Type</th><th>Shift Total (KES)</th></tr></thead>
              <tbody>
                {cfg.payment_methods.filter((m) => m.active !== false).map((m) => (
                  <tr key={m.id}>
                    <td className="!text-left font-medium">{m.name}</td>
                    <td className="!text-left text-xs text-muted-foreground">{m.kind}</td>
                    <td className="calc-cell">{kes(t.payments_by_method?.[m.id] || 0)}</td>
                  </tr>
                ))}
                <tr className="font-semibold"><td className="!text-left" colSpan={2}>Payments Total</td><td className="calc-cell">{kes(t.payments_total)}</td></tr>
              </tbody>
            </table>
          </div>
          <Card className="p-5">
            <h4 className="font-display font-semibold text-slate-800">Cash Reconciliation</h4>
            <dl className="mt-4 space-y-2 text-sm">
              <Row label="Total Sales" value={kes(t.total_sales)} />
              <Row label="Less: Non-cash collections" value={`(${kes(t.non_cash_total)})`} />
              <Row label="Less: Expenses" value={`(${kes(t.expenses_total)})`} />
              <div className="border-t pt-2"><Row label="Expected Cash" value={kes(t.expected_cash)} bold /></div>
              <div className="border-t pt-2"><Row label="Cash Declared (sum of attendant Cash entries)" value={kes(t.actual_cash)} bold /></div>
              {t.topups_total > 0 && <Row label="Rubis Card top-ups received" value={kes(t.topups_total)} />}
              {t.account_cash_received > 0 && (
                <>
                  <Row label="Add: account payments and top-ups in cash" value={kes(t.account_cash_received)} />
                  <Row label="Cash to bank" value={kes(t.cash_to_bank)} bold />
                </>
              )}
              <div className={`mt-2 flex items-center justify-between rounded-lg px-3 py-2 ${t.cash_status === "within" ? "var-within" : t.cash_status === "warning" ? "var-warning" : "var-critical"}`}>
                <span className="text-sm font-medium">Cash Variance</span>
                <span className="font-mono-fin font-bold" data-testid="cash-variance-display">{t.cash_variance >= 0 ? "+" : ""}{kes(t.cash_variance)}</span>
              </div>
            </dl>
          </Card>
        </div>
      </Section>

      <InvoiceLines cfg={cfg} entry={entry} set={set} readOnly={readOnly} attendants={attendants} />

      {/* Section E: Expenses */}
      {(() => {
        const customers = (cfg.receivable_accounts || []).filter((a) => a.kind === "customer" && (a.active !== false || (entry.account_receipts || []).some((x) => x.account_id === a.id)));
        const payMethods = [...(cfg.payment_methods || []).filter((m) => ["cash", "mobile_money", "card"].includes(m.kind) && m.active !== false && !/rubis/i.test(m.name || ""))];
        const methodById = Object.fromEntries([...(cfg.payment_methods || []), ...payMethods].map((m) => [m.id, m]));
        const hints = (computed.account_receipt_hints || []).filter((h) => !(entry.account_receipts || []).some((a) => a.till_id === h.till_id));
        const mpesa = payMethods.find((m) => m.kind === "mobile_money");
        const blank = (extra = {}) => ({ _k: uid(), account_id: null, method_id: null, till_id: null, amount: "", reference: "", note: "", ...extra });
        return (
          <Section title="Account payments & Rubis Card top-ups received" icon={Wallet}>
            <p className="text-xs text-muted-foreground">
              A credit customer paid their account here — by M-Pesa, cash or card. It is not a sale: M-Pesa goes into that till&apos;s expected balance,
              cash is banked with the shift&apos;s cash, and the customer&apos;s balance in Debtors goes down automatically.
              A <b>Rubis Card top-up</b> (a customer loading their Rubis Card here) goes here too, as &ldquo;Rubis Card top-up&rdquo; with the card
              number as reference — it is posted against the Rubis account, never as a sale.
              Attendants don&apos;t put top-up money in their own columns.
            </p>
            {!readOnly && hints.map((h) => (
              <div key={h.till_id} className="mt-2 flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="account-receipt-hint">
                <span><b>{tillById[h.till_id]?.label}</b> is over by {kes(h.amount)} and no other till is short by the same — was this a customer paying their account?</span>
                {mpesa && <Button size="sm" variant="outline" className="h-7" onClick={() => set((n) => {
                  n.account_receipts = [...(n.account_receipts || []), blank({ method_id: mpesa.id, till_id: h.till_id, amount: h.amount })];
                })(null)}>Record as account payment</Button>}
              </div>
            ))}
            {(entry.account_receipts || []).length > 0 && (
              <table className="recon-table mt-2">
                <thead><tr><th className="text-left">Customer</th><th className="text-left">Paid by</th><th className="text-left">Into till / bank</th><th>Amount</th>
                  <th className="text-left">Reference (M-Pesa code / slip / receipt)</th><th className="text-left">Note</th><th className="w-10" /></tr></thead>
                <tbody>
                  {entry.account_receipts.map((a, idx) => {
                    const isMpesa = methodById[a.method_id]?.kind === "mobile_money";
                    const sel = (field, value, opts, placeholder, bad) => (
                      <select className={`grid-input !text-left ${bad ? "!border-rose-300 !bg-rose-50" : ""}`} style={{ textAlign: "left" }} disabled={readOnly} value={value || ""}
                        onChange={(e) => set((n, v) => {
                          n.account_receipts[idx][field] = v || null;
                          if (field === "method_id" && methodById[v]?.kind !== "mobile_money") n.account_receipts[idx].till_id = null;
                          if (field === "method_id" && !["cheque", "transfer"].includes(v)) n.account_receipts[idx].bank = null;
                        })(e.target.value)}>
                        <option value="">{placeholder}</option>
                        {opts.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                      </select>
                    );
                    return (
                      <tr key={a._k}>
                        <td className="!text-left">{sel("account_id", a.account_id, [...(cfg.receivable_accounts || []).filter((x) => x.kind === "card" && /rubis/i.test(x.name || "") && x.active !== false).map((x) => [x.id, "Rubis Card top-up"]),
                          ...customers.map((c) => [c.id, c.name])], "— customer / top-up —", !a.account_id)}</td>
                        <td className="!text-left">{sel("method_id", a.method_id, payMethods.map((m) => [m.id, m.name]), "— method —", !a.method_id)}</td>
                        <td className="!text-left">{isMpesa ? sel("till_id", a.till_id, entry.till_ledger.map((tl) => [tl.till_id, tillById[tl.till_id]?.label]), "— till —", !a.till_id)
                          : ["cheque", "transfer"].includes(a.method_id) ? sel("bank", a.bank, RECEIPT_BANKS.map((b) => [b, b]), "— bank —", !a.bank)
                          : <span className="text-xs text-muted-foreground">—</span>}</td>
                        <td><NumCell value={a.amount} disabled={readOnly} tid={`acct-amount-${idx}`} onChange={set((n, v) => (n.account_receipts[idx].amount = v))} /></td>
                        <td className="!text-left"><input className={`grid-input !text-left ${!a.reference && !a.note ? "!border-rose-300 !bg-rose-50" : ""}`} style={{ textAlign: "left" }} disabled={readOnly} value={a.reference || ""}
                          onChange={(e) => set((n, v) => (n.account_receipts[idx].reference = v))(e.target.value)} /></td>
                        <td className="!text-left"><input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={a.note || ""}
                          onChange={(e) => set((n, v) => (n.account_receipts[idx].note = v))(e.target.value)} /></td>
                        <td className="text-center">{!readOnly && <button onClick={() => set((n) => n.account_receipts.splice(idx, 1))(null)} className="text-rose-500">×</button>}</td>
                      </tr>
                    );
                  })}
                  <tr className="font-semibold"><td className="!text-left" colSpan={3}>Total account payments</td><td className="calc-cell">{kes(t.account_receipts_total)}</td><td colSpan={3} /></tr>
                </tbody>
              </table>
            )}
            {!readOnly && (
              <Button variant="outline" size="sm" className="mt-2" data-testid="add-account-receipt"
                onClick={() => set((n) => { n.account_receipts = [...(n.account_receipts || []), blank()]; })(null)}>
                + Add account payment
              </Button>
            )}
          </Section>
        );
      })()}
      <Section title="E · Expenses & Operational Payouts" icon={Receipt}>
        <table className="recon-table">
          <thead><tr><th className="text-left">Category</th><th className="text-left">Description / Payee</th><th className="text-left">Voucher No.</th><th>Amount (KES)</th><th className="w-10" /></tr></thead>
          <tbody>
            {entry.expenses.map((ex, idx) => (
              <tr key={ex._k || idx}>
                <td className="!text-left">
                  <select
                    className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly}
                    value={ex.category || ""} data-testid={`exp-cat-${idx}`}
                    onChange={(e) => set((n, v) => (n.expenses[idx].category = v))(e.target.value)}
                  >
                    <option value="">— select category —</option>
                    {(cfg.expense_categories || []).filter((c) => c.active !== false).map((c) => (
                      <option key={c.id} value={c.label}>{c.label}</option>
                    ))}
                  </select>
                </td>
                <td className="!text-left"><input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={ex.description || ""} data-testid={`exp-desc-${idx}`} onChange={(e) => set((n, v) => (n.expenses[idx].description = v))(e.target.value)} /></td>
                <td className="!text-left"><input className="grid-input !text-left" style={{ textAlign: "left" }} disabled={readOnly} value={ex.voucher_no || ""} onChange={(e) => set((n, v) => (n.expenses[idx].voucher_no = v))(e.target.value)} /></td>
                <td><NumCell value={ex.amount} disabled={readOnly} tid={`exp-amount-${idx}`} onChange={set((n, v) => (n.expenses[idx].amount = v))} /></td>
                <td className="text-center">{!readOnly && <button onClick={() => set((n) => n.expenses.splice(idx, 1))(null)} className="text-rose-500" data-testid={`exp-remove-${idx}`}>×</button>}</td>
              </tr>
            ))}
            <tr className="font-semibold"><td className="!text-left" colSpan={3}>Expenses Total</td><td className="calc-cell">{kes(t.expenses_total)}</td><td /></tr>
          </tbody>
        </table>
        {!readOnly && (
          <Button variant="outline" size="sm" className="mt-3" onClick={() => set((n) => n.expenses.push({ category: "", description: "", voucher_no: "", amount: "", _k: uid() }))(null)} data-testid="add-expense-button">
            + Add Expense
          </Button>
        )}
        <div className="mt-4 space-y-1.5">
          <Label>Shift Notes</Label>
          <Textarea disabled={readOnly} value={entry.notes} onChange={(e) => set((n, v) => (n.notes = v))(e.target.value)} data-testid="shift-notes" placeholder="Any handover notes or exceptions…" />
        </div>
      </Section>

      {/* Section F: M-Pesa till reconciliation — same model as tank dips: opening carries forward,
          M-Pesa received is pulled from the assigned attendant's M-Pesa total in Section D (never
          re-entered), withdrawal is the only manual input, closing is computed. Actual Balance is
          an optional physical check (e.g. dialing the till) — same role as a tank's physical dip —
          producing a variance against the calculated closing, since payment and recorded
          consumption can land at slightly different times. */}
      {entry.till_ledger.length > 0 && (
        <Section title="F · M-Pesa Till Reconciliation" icon={Smartphone}>
          <div className="overflow-x-auto">
            <table className="recon-table">
              <thead>
                <tr>
                  <th className="text-left">Till</th>
                  <th className="text-left">Attendant</th>
                  <th>Opening Balance</th><th>M-Pesa Received</th><th>Withdrawal</th><th>Closing Balance</th>
                  <th>Actual Balance</th><th>Variance</th><th className="text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {entry.till_ledger.map((r, idx) => {
                  const till = tillById[r.till_id];
                  const line = computed.till_lines.find((l) => l.till_id === r.till_id) || {};
                  return (
                    <tr key={r.till_id}>
                      <td className="!text-left font-medium">{till?.label} <span className="text-xs text-muted-foreground">({till?.till_number})</span></td>
                      <td className="!text-left">
                        {/* everyone paying into this till (several attendants can share one) */}
                        <div className="flex flex-wrap gap-1" data-testid={`till-attendant-${r.till_id}`}>
                          {attendants.map((a) => {
                            const raw = entry.till_attendants?.[r.till_id];
                            const on = (Array.isArray(raw) ? raw : raw ? [raw] : []).includes(a.id);
                            return (
                              <button key={a.id} type="button" disabled={readOnly} aria-pressed={on}
                                onClick={() => set((n) => {
                                  n.till_attendants = { ...(n.till_attendants || {}) };
                                  const cur = n.till_attendants[r.till_id];
                                  const list = Array.isArray(cur) ? cur : cur ? [cur] : [];
                                  n.till_attendants[r.till_id] = on ? list.filter((x) => x !== a.id) : [...list, a.id];
                                })(null)}
                                className={`rounded-full border px-2 py-0.5 text-xs ${on ? "border-brand bg-brand/10 font-semibold text-slate-900" : "border-slate-200 text-slate-500 hover:bg-slate-50"} ${readOnly && !on ? "hidden" : ""}`}>
                                {a.name}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td><NumCell value={r.opening_balance} disabled={readOnly || r._openingLocked} tid={`till-opening-${r.till_id}`} onChange={set((n, v) => (n.till_ledger[idx].opening_balance = v))} /></td>
                      <td className="calc-cell">{kes(line.mpesa_received)}</td>
                      <td><NumCell value={r.withdrawal} disabled={readOnly} tid={`till-withdrawal-${r.till_id}`} onChange={set((n, v) => (n.till_ledger[idx].withdrawal = v))} /></td>
                      <td className="calc-cell font-semibold">{kes(line.closing_balance)}</td>
                      <td><NumCell value={r.actual_balance} disabled={readOnly} tid={`till-actual-${r.till_id}`} onChange={set((n, v) => (n.till_ledger[idx].actual_balance = v))} /></td>
                      <td className="calc-cell">{line.variance === null || line.variance === undefined ? "—" : `${line.variance >= 0 ? "+" : ""}${kes(line.variance)}`}</td>
                      <td className="!text-left">{line.status ? <VarianceBadge status={line.status}>{line.status}</VarianceBadge> : <span className="text-xs text-muted-foreground">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}
        </TabsContent>
      </Tabs>

      {/* Sticky action bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/90 backdrop-blur-md shadow-lg">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {dirty ? <span className="text-amber-600">● Unsaved changes</span> : <span>All changes saved</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={openAudit} data-testid="audit-drawer-toggle"><History className="mr-1.5 h-4 w-4" />Audit</Button>
            <Button variant="outline" size="sm" onClick={() => setItemizeOpen(true)} data-testid="itemize-button"><ListChecks className="mr-1.5 h-4 w-4" />Itemize</Button>
            {readOnly && <Button variant="outline" size="sm" onClick={openAdjustments} data-testid="corrections-button"><ShieldAlert className="mr-1.5 h-4 w-4" />Corrections</Button>}
            <Button variant="outline" size="sm" onClick={() => download("excel")} data-testid="export-excel-button"><FileSpreadsheet className="mr-1.5 h-4 w-4" />Excel</Button>
            <Button variant="outline" size="sm" onClick={() => download("pdf")} data-testid="export-pdf-button"><FileText className="mr-1.5 h-4 w-4" />PDF</Button>

            {!readOnly && (
              <>
                <Button size="sm" variant="secondary" onClick={save} disabled={saving} data-testid="save-draft-button">
                  {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Save Draft
                </Button>
                <Button size="sm" className="bg-brand hover:bg-brand/90" onClick={() => setTransition({ action: "submit", title: "Submit Shift" })} data-testid="submit-shift-button">
                  <Send className="mr-1.5 h-4 w-4" />Submit
                </Button>
              </>
            )}
            {shift.status === "submitted" && isManager && (
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => setTransition({ action: "manager-review", title: "Confirm Manager Review" })} data-testid="manager-review-button">
                <ClipboardCheck className="mr-1.5 h-4 w-4" />Manager Review
              </Button>
            )}
            {shift.status === "manager_reviewed" && isAccountant && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setTransition({ action: "accountant-approve", title: "Accountant Approval" })} data-testid="accountant-approve-button">
                <CheckCircle2 className="mr-1.5 h-4 w-4" />Approve
              </Button>
            )}
            {shift.status === "accountant_approved" && isAccountant && (
              <Button size="sm" className="bg-indigo-950 hover:bg-indigo-900" onClick={() => setTransition({ action: "lock", title: "Lock Shift" })} data-testid="lock-shift-button">
                <Lock className="mr-1.5 h-4 w-4" />Lock
              </Button>
            )}
            {readOnly && isAccountant && (
              <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setTransition({ action: "reopen", title: "Reopen Shift (Audited)" })} data-testid="reopen-shift-button">
                <Unlock className="mr-1.5 h-4 w-4" />Reopen
              </Button>
            )}
          </div>
        </div>
      </div>

      <AuditTrailDrawer open={auditOpen} onOpenChange={setAuditOpen} logs={logs} />

      {/* Transition confirm dialog */}
      <Dialog open={!!transition} onOpenChange={(o) => !o && setTransition(null)}>
        <DialogContent data-testid="transition-dialog">
          <DialogHeader><DialogTitle className="font-display">{transition?.title}</DialogTitle><DialogDescription>This action is recorded in the audit trail.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              {transition?.action === "reopen"
                ? "Reopening returns the shift to draft and is fully audited. A reason is mandatory."
                : "Add an optional note. This action is recorded in the audit trail."}
            </p>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={transition?.action === "reopen" ? "Reason for reopening (required)" : "Note (optional)"} data-testid="transition-note" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTransition(null)}>Cancel</Button>
            <Button onClick={runTransition} disabled={busy || (transition?.action === "reopen" && !note.trim())} className="bg-brand hover:bg-brand/90" data-testid="transition-confirm">
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AdjustmentsDialog open={adjOpen} onOpenChange={setAdjOpen} shiftId={id} adjustments={adjustments} reload={openAdjustments} user={user} shift={shift} cfg={cfg} />
      <ItemizationDialog open={itemizeOpen} onOpenChange={setItemizeOpen} entry={entry} set={set} attendants={attendants} methodById={methodById} readOnly={readOnly} />
    </div>
  );
}

function num(v) {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

// A row's opening value is locked once it's actually been set (carried forward from the previous
// shift, or saved once before) — only a brand-new pump/tank/item/till with no history yet is
// still blank and editable. Prevents quietly tampering with the reconciliation baseline; use
// Corrections (audited + approved) for a genuine fix.
function hasValue(v) {
  return v !== undefined && v !== null && v !== "";
}

// banks a customer's cheque or transfer can be paid into (the books match the bank by this name)
const RECEIPT_BANKS = ["ABSA", "KCB"];

function uid() {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random());
}

// Credit sales itemized per invoice: each attendant's Invoice total broken into customer invoices
// (customer, invoice/LPO no., vehicle, product, litres). Feeds the debtors ledger (Accounting ->
// Debtors & settlements); any part not itemized waits there under "Invoices not itemized".
function InvoiceLines({ cfg, entry, set, readOnly, attendants }) {
  const linked = new Set((cfg.receivable_accounts || []).filter((a) => a.method_id).map((a) => a.method_id));
  const invoiceMethods = cfg.payment_methods.filter((m) => m.kind === "invoice" && m.active !== false && !linked.has(m.id));
  const customers = (cfg.receivable_accounts || []).filter((a) => a.kind === "customer" && a.active !== false && !a.method_id);
  if (!invoiceMethods.length) return null;
  const ids = new Set(invoiceMethods.map((m) => m.id));
  // each attendant's declared Invoice total vs what is itemized to customers
  const checks = attendants.map((a) => {
    const declared = entry.payments.filter((p) => p.attendant_id === a.id && ids.has(p.method_id)).reduce((t, p) => t + num(p.amount), 0);
    const itemized = entry.invoice_lines.filter((l) => l.attendant_id === a.id && l.customer_id).reduce((t, l) => t + num(l.amount), 0);
    return { id: a.id, name: a.name, declared, itemized, difference: declared - itemized };
  }).filter((c) => c.declared || c.itemized);
  if (!checks.length && !entry.invoice_lines.length) return null;
  const setLn = (idx, field) => (v) => set((n) => { n.invoice_lines[idx][field] = v; })(null);
  const fillAmount = (idx, litres, productId) => {
    const price = num(entry.fuel_prices?.[productId]);
    if (num(litres) && price) set((n) => { n.invoice_lines[idx].amount = String(Math.round(num(litres) * price * 100) / 100); })(null);
  };
  const cell = "grid-input !text-left";
  return (
    <Section title="Invoices (credit sales)" icon={FileText}>
      <div className="mb-3 flex flex-wrap gap-2" data-testid="invoice-checks">
        {checks.map((c) => {
          const ok = Math.abs(c.difference) < 0.5;
          return (
            <span key={c.id} className={`rounded-lg border px-3 py-1.5 text-xs ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-900"}`}>
              <span className="font-semibold">{c.name}</span> · invoices {kes(c.declared)} · itemized {kes(c.itemized)}
              {ok ? " ✓" : c.difference > 0 ? ` · ${kes(c.difference)} still to itemize` : ` · itemized ${kes(-c.difference)} MORE than declared`}
            </span>
          );
        })}
      </div>
      <div className="overflow-x-auto">
        <table className="recon-table">
          <thead><tr>
            <th className="text-left">Attendant</th><th className="text-left">Customer</th><th className="text-left">Invoice / LPO no.</th>
            <th className="text-left">Vehicle reg</th><th className="text-left">Product</th><th>Litres</th><th>Amount (KES)</th><th className="w-10" />
          </tr></thead>
          <tbody>
            {entry.invoice_lines.length === 0 && (
              <tr><td colSpan={8} className="py-4 text-center text-sm text-muted-foreground">No invoices itemized yet — add one line per invoice.</td></tr>
            )}
            {entry.invoice_lines.map((ln, idx) => (
              <tr key={ln._k} data-testid={`inv-line-${idx}`}>
                <td className="!text-left">
                  <select className={cell} style={{ textAlign: "left" }} disabled={readOnly} value={ln.attendant_id || ""} onChange={(e) => setLn(idx, "attendant_id")(e.target.value)}>
                    <option value="">— who? —</option>
                    {attendants.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </td>
                <td className="!text-left">
                  <select className={cell} style={{ textAlign: "left" }} disabled={readOnly} value={ln.customer_id || ""} onChange={(e) => setLn(idx, "customer_id")(e.target.value)} data-testid={`inv-customer-${idx}`}>
                    <option value="">— customer —</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </td>
                <td className="!text-left"><input className={cell} style={{ textAlign: "left" }} disabled={readOnly} value={ln.invoice_no || ""} onChange={(e) => setLn(idx, "invoice_no")(e.target.value)} /></td>
                <td className="!text-left"><input className={cell} style={{ textAlign: "left" }} disabled={readOnly} value={ln.vehicle_reg || ""} onChange={(e) => setLn(idx, "vehicle_reg")(e.target.value.toUpperCase())} /></td>
                <td className="!text-left">
                  <select className={cell} style={{ textAlign: "left" }} disabled={readOnly} value={ln.product_id || ""}
                    onChange={(e) => { setLn(idx, "product_id")(e.target.value); fillAmount(idx, ln.litres, e.target.value); }}>
                    <option value="">—</option>
                    {cfg.products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </td>
                <td><NumCell value={ln.litres ?? ""} disabled={readOnly} tid={`inv-litres-${idx}`} onChange={(v) => { setLn(idx, "litres")(v); fillAmount(idx, v, ln.product_id); }} /></td>
                <td><NumCell value={ln.amount ?? ""} disabled={readOnly} tid={`inv-amount-${idx}`} onChange={setLn(idx, "amount")} /></td>
                <td className="text-center">{!readOnly && <button onClick={() => set((n) => n.invoice_lines.splice(idx, 1))(null)} className="text-rose-500" aria-label="Remove invoice">×</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button variant="outline" size="sm" data-testid="add-invoice-line"
            onClick={() => set((n) => n.invoice_lines.push({ _k: uid(), attendant_id: "", method_id: invoiceMethods[0].id,
              customer_id: "", invoice_no: "", vehicle_reg: "", product_id: "", litres: "", amount: "" }))(null)}>
            + Add invoice
          </Button>
          {customers.length === 0 && <span className="text-xs text-amber-700">Add your credit customers first in Station Setup → Customers &amp; Accounts.</span>}
          <span className="text-xs text-muted-foreground">Litres × the shift price fills the amount; adjust it if the invoice differs.</span>
        </div>
      )}
    </Section>
  );
}

function NumCell({ value, onChange, disabled, tid }) {
  return <input type="number" className="grid-input" value={value} disabled={disabled} data-testid={tid} onChange={(e) => onChange(e.target.value)} />;
}

function Section({ title, icon: Icon, children }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b bg-slate-50 px-5 py-3">
        <Icon className="h-4 w-4 text-brand" />
        <h3 className="font-display text-base font-semibold text-slate-800">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </Card>
  );
}

function SummaryStat({ label, value, icon: Icon, status }) {
  const color = status === "critical" ? "text-rose-600" : status === "warning" ? "text-amber-600" : "text-slate-900";
  return (
    <Card className="p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className={`mt-1 font-mono-fin text-sm font-bold ${color}`}>{value}</div>
    </Card>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`font-mono-fin ${bold ? "font-bold text-slate-900" : "text-slate-700"}`}>{value}</dd>
    </div>
  );
}

// Defines what can be corrected: how to list candidate rows for a section (matched by a stable
// id, never array index, since a row's position can shift) and which fields of that row are
// editable. Drives the structured correction picker below.
const ADJUSTMENT_SECTIONS = {
  fuel_readings: {
    label: "Fuel Pump Reading",
    rows: (shift, cfg) => (shift.fuel_readings || []).map((row) => ({
      match: { pump_id: row.pump_id }, row,
      label: cfg.pumps.find((p) => p.id === row.pump_id)?.label || row.pump_id,
    })),
    fields: [
      { key: "opening", label: "Manual Opening (L)" }, { key: "closing", label: "Manual Closing (L)" },
      { key: "testing", label: "Testing (L)" },
      { key: "elec_opening_l", label: "Electronic Opening (L)" }, { key: "elec_closing_l", label: "Electronic Closing (L)" },
      { key: "elec_opening_kes", label: "Electronic Opening (KES)" }, { key: "elec_closing_kes", label: "Electronic Closing (KES)" },
    ],
  },
  tank_dips: {
    label: "Tank Dip",
    rows: (shift, cfg) => (shift.tank_dips || []).map((row) => ({
      match: { tank_id: row.tank_id }, row,
      label: cfg.tanks.find((t) => t.id === row.tank_id)?.label || row.tank_id,
    })),
    fields: [
      { key: "opening_dip", label: "Opening Dip" }, { key: "deliveries", label: "Deliveries" },
      { key: "rtt", label: "RTT" }, { key: "closing_dip", label: "Closing Dip" },
    ],
  },
  nonfuel: {
    label: "Non-Fuel Stock",
    rows: (shift, cfg) => (shift.nonfuel || []).map((row) => ({
      match: { item_id: row.item_id }, row,
      label: cfg.nonfuel_items.find((i) => i.id === row.item_id)?.name || row.item_id,
    })),
    fields: [
      { key: "opening", label: "Opening" }, { key: "received", label: "Received" }, { key: "closing", label: "Closing" },
      { key: "unit_price", label: "Unit Price" }, { key: "cost_price", label: "Cost Price" },
    ],
  },
  payments: {
    label: "Attendant Payment (Section D)",
    rows: (shift, cfg) => (shift.payments || []).map((row) => ({
      match: { attendant_id: row.attendant_id || null, method_id: row.method_id }, row,
      label: `${cfg.attendants.find((a) => a.id === row.attendant_id)?.name || "Unassigned / Shop"} — ${cfg.payment_methods.find((m) => m.id === row.method_id)?.name || row.method_id}`,
    })),
    fields: [{ key: "amount", label: "Amount" }, { key: "reference", label: "Reference" }],
  },
  nonfuel_payments: {
    label: "Non-Fuel Payment (Lubes/LPG)",
    rows: (shift, cfg) => (shift.nonfuel_payments || []).map((row) => ({
      match: { group: row.group, method_id: row.method_id }, row,
      label: `${row.group === "lube" ? "Lubes" : "LPG & Accessories"} — ${cfg.payment_methods.find((m) => m.id === row.method_id)?.name || row.method_id}`,
    })),
    fields: [{ key: "amount", label: "Amount" }, { key: "reference", label: "Reference" }],
  },
  till_ledger: {
    label: "Till Reconciliation",
    rows: (shift, cfg) => (shift.till_ledger || []).map((row) => ({
      match: { till_id: row.till_id }, row,
      label: cfg.tills.find((t) => t.id === row.till_id)?.label || row.till_id,
    })),
    fields: [
      { key: "opening_balance", label: "Opening Balance" }, { key: "withdrawal", label: "Withdrawal" },
      { key: "actual_balance", label: "Actual Balance" },
    ],
  },
  account_receipts: {
    label: "Account payment received",
    rows: (shift, cfg) => (shift.account_receipts || []).map((row) => ({
      match: { _k: row._k }, row,
      label: `${(cfg.receivable_accounts || []).find((a) => a.id === row.account_id)?.name || "?"} — ${cfg.payment_methods.find((m) => m.id === row.method_id)?.name || "?"}`,
    })),
    fields: [{ key: "amount", label: "Amount" }, { key: "reference", label: "Reference" }, { key: "note", label: "Note" }],
  },
  expenses: {
    label: "Expense",
    rows: (shift) => (shift.expenses || []).map((row, idx) => ({
      match: { _k: row._k || String(idx) }, row,
      label: `${row.category || "—"} — ${row.description || ""}`,
    })),
    fields: [
      { key: "category", label: "Category" }, { key: "description", label: "Description" },
      { key: "voucher_no", label: "Voucher No." }, { key: "amount", label: "Amount" },
    ],
  },
  tank_deliveries: {
    label: "Fuel Delivery",
    rows: (shift, cfg) => (shift.tank_deliveries || []).map((row, idx) => ({
      match: { _k: row._k || String(idx) }, row,
      label: `${cfg.tanks.find((t) => t.id === row.tank_id)?.label || row.tank_id} — Inv# ${row.invoice_no || "—"}`,
    })),
    fields: [
      { key: "invoice_no", label: "Invoice No." }, { key: "date", label: "Date" },
      { key: "invoice_amount", label: "Invoice Amount" }, { key: "invoice_volume", label: "Invoice Volume" },
      { key: "offloaded_quantity", label: "Offloaded Quantity" },
    ],
  },
  nonfuel_deliveries: {
    label: "Dry Stock Delivery",
    rows: (shift, cfg) => (shift.nonfuel_deliveries || []).map((row, idx) => ({
      match: { _k: row._k || String(idx) }, row,
      label: `${cfg.nonfuel_items.find((i) => i.id === row.item_id)?.name || row.item_id} — Inv# ${row.invoice_no || "—"}`,
    })),
    fields: [
      { key: "invoice_no", label: "Invoice No." }, { key: "date", label: "Date" },
      { key: "invoice_amount", label: "Invoice Amount" }, { key: "invoice_quantity", label: "Invoice Quantity" },
      { key: "actual_quantity", label: "Actual Quantity" },
    ],
  },
};

function AdjustmentsDialog({ open, onOpenChange, shiftId, adjustments, reload, user, shift, cfg }) {
  const [sectionKey, setSectionKey] = useState("fuel_readings");
  const [rowIdx, setRowIdx] = useState("");
  const [fieldKey, setFieldKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const isAccountant = canAccount(user);

  const sectionDef = ADJUSTMENT_SECTIONS[sectionKey];
  const rows = shift && cfg ? sectionDef.rows(shift, cfg) : [];
  const selectedRow = rowIdx !== "" ? rows[Number(rowIdx)] : null;
  const fieldDef = sectionDef.fields.find((f) => f.key === fieldKey);
  const originalValue = selectedRow && fieldKey ? selectedRow.row[fieldKey] : "";

  const resetPickers = (nextSection) => {
    setSectionKey(nextSection);
    setRowIdx(""); setFieldKey(""); setNewValue("");
  };

  const submit = async () => {
    if (!selectedRow || !fieldDef || !reason.trim()) return toast.error("Pick a row, a field, and give a reason");
    setBusy(true);
    try {
      await api.post(`/shifts/${shiftId}/adjustments`, {
        field_label: `${sectionDef.label}: ${selectedRow.label} → ${fieldDef.label}`,
        section: sectionKey, match: selectedRow.match, field: fieldKey,
        original_value: originalValue, new_value: newValue, reason,
      });
      toast.success("Correction request raised");
      setRowIdx(""); setFieldKey(""); setNewValue(""); setReason("");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (adjId, decision) => {
    try {
      await api.post(`/shifts/${shiftId}/adjustments/${adjId}/decide`, { decision });
      toast.success(decision === "approve" ? "Correction approved and applied to the shift" : "Correction rejected");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="adjustments-dialog">
        <DialogHeader><DialogTitle className="font-display">Controlled Corrections</DialogTitle><DialogDescription>Pick exactly what to correct; an accountant approves it, and approving writes the change straight into the shift. Fully audited.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border bg-slate-50 p-4">
            <h4 className="mb-3 text-sm font-semibold text-slate-800">Raise a correction request</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Section</Label>
                <select className="grid-input !text-left" style={{ textAlign: "left" }} value={sectionKey} data-testid="adj-section-select" onChange={(e) => resetPickers(e.target.value)}>
                  {Object.entries(ADJUSTMENT_SECTIONS).map(([k, d]) => <option key={k} value={k}>{d.label}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Row</Label>
                <select className="grid-input !text-left" style={{ textAlign: "left" }} value={rowIdx} data-testid="adj-row-select" onChange={(e) => { setRowIdx(e.target.value); setFieldKey(""); setNewValue(""); }}>
                  <option value="">— select —</option>
                  {rows.map((r, i) => <option key={i} value={i}>{r.label}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Field</Label>
                <select className="grid-input !text-left" style={{ textAlign: "left" }} value={fieldKey} disabled={!selectedRow} data-testid="adj-field-select" onChange={(e) => { setFieldKey(e.target.value); setNewValue(""); }}>
                  <option value="">— select —</option>
                  {sectionDef.fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Original value</Label><Input value={String(originalValue ?? "—")} disabled data-testid="adj-original-input" /></div>
              <div className="space-y-1.5 col-span-2"><Label className="text-xs">Proposed value</Label><Input value={newValue} onChange={(e) => setNewValue(e.target.value)} disabled={!fieldDef} data-testid="adj-new-input" /></div>
            </div>
            <div className="mt-3 space-y-1.5"><Label className="text-xs">Reason (required)</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adj-reason-input" /></div>
            <Button onClick={submit} disabled={busy} className="mt-3 bg-brand hover:bg-brand/90" size="sm" data-testid="adj-submit-button">
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Submit request
            </Button>
          </div>

          <div>
            <h4 className="mb-2 text-sm font-semibold text-slate-800">Correction history</h4>
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {adjustments.length === 0 && <p className="text-sm text-muted-foreground">No correction requests.</p>}
              {adjustments.map((a) => (
                <div key={a._id} className="rounded-lg border p-3 text-sm" data-testid={`adjustment-${a._id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{a.field_label}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${a.status === "approved" ? "bg-emerald-100 text-emerald-800" : a.status === "rejected" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>{a.status}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    <span className="font-mono-fin text-rose-600 line-through">{String(a.original_value ?? "—")}</span>→
                    <span className="font-mono-fin text-emerald-700">{String(a.new_value ?? "—")}</span>
                  </div>
                  <div className="mt-1 text-xs italic text-slate-600">"{a.reason}"</div>
                  <div className="mt-1 text-[11px] text-muted-foreground">by {a.requested_by}</div>
                  {a.status === "pending" && isAccountant && (
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(a._id, "approve")} data-testid={`adj-approve-${a._id}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="h-7 border-rose-300 text-rose-700" onClick={() => decide(a._id, "reject")} data-testid={`adj-reject-${a._id}`}>Reject</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const NONFUEL_GROUP_LABELS = { "nonfuel:lube": "Lubes", "nonfuel:lpg_accessory": "LPG & Accessories" };

function ItemizationDialog({ open, onOpenChange, entry, set, attendants, methodById, readOnly }) {
  const groups = [];
  const seen = new Set();
  entry.payments.forEach((p) => {
    const amt = num(p.amount);
    const key = `${p.attendant_id || ""}|${p.method_id}`;
    // cash goes to the bank and M-Pesa is checked against the M-Pesa statements - not itemized here
    if (["cash", "mobile_money"].includes(methodById[p.method_id]?.kind)) return;
    if (amt === 0 || seen.has(key)) return;
    seen.add(key);
    groups.push({ attendant_id: p.attendant_id || "", method_id: p.method_id, lump: amt });
  });
  // Lubes and LPG & Accessories are paid on their own tab - itemized per product group
  const nonfuel = {};
  (entry.nonfuel_payments || []).forEach((p) => {
    if (["cash", "mobile_money"].includes(methodById[p.method_id]?.kind) || !num(p.amount)) return;
    const key = `nonfuel:${p.group}|${p.method_id}`;
    nonfuel[key] = nonfuel[key] || { attendant_id: `nonfuel:${p.group}`, method_id: p.method_id, lump: 0 };
    nonfuel[key].lump += num(p.amount);
  });
  groups.push(...Object.values(nonfuel));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="itemization-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Itemize Transactions</DialogTitle>
          <DialogDescription>
            Break each payment method total into individual transactions (till/reference numbers) to match against statements later. Optional — doesn't change shift totals.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] space-y-4 overflow-y-auto py-2">
          {groups.length === 0 && (
            <p className="text-sm text-muted-foreground">No non-zero payment amounts yet — fill in Section D first.</p>
          )}
          {groups.map((g) => {
            const attName = NONFUEL_GROUP_LABELS[g.attendant_id] || (g.attendant_id ? (attendants.find((a) => a.id === g.attendant_id)?.name || g.attendant_id) : "Unassigned / Shop");
            const methodName = methodById[g.method_id]?.name || g.method_id;
            const rows = entry.payment_transactions
              .map((t, idx) => ({ ...t, idx }))
              .filter((t) => (t.attendant_id || "") === g.attendant_id && t.method_id === g.method_id);
            const itemizedTotal = rows.reduce((a, r) => a + num(r.amount), 0);
            const variance = itemizedTotal - g.lump;
            const matched = Math.abs(variance) < 0.01;
            return (
              <div key={`${g.attendant_id || "u"}|${g.method_id}`} className="overflow-hidden rounded-lg border" data-testid={`itemize-group-${g.attendant_id || "unassigned"}-${g.method_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-slate-50 px-4 py-2.5">
                  <div className="text-sm font-semibold text-slate-800">{attName} — {methodName}</div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span>Lump Total: <span className="font-mono-fin font-semibold text-slate-700">KSh {kes(g.lump)}</span></span>
                    <span>Itemized: <span className="font-mono-fin font-semibold text-slate-700">KSh {kes(itemizedTotal)}</span></span>
                    <span className={`font-mono-fin font-semibold ${matched ? "text-emerald-600" : "text-rose-600"}`}>
                      {matched ? "Matched" : `Variance: ${variance >= 0 ? "+" : ""}${kes(variance)}`}
                    </span>
                  </div>
                </div>
                <div className="space-y-2 p-3">
                  {rows.map((r) => (
                    <div key={r._k || r.idx} className="flex items-center gap-2">
                      <Input type="number" step="0.01" inputMode="decimal" className="w-32 font-mono-fin" disabled={readOnly} value={r.amount} placeholder="Amount (with cents)"
                        data-testid={`itemize-amount-${g.attendant_id || "unassigned"}-${g.method_id}-${r.idx}`}
                        onChange={(e) => set((n, v) => (n.payment_transactions[r.idx].amount = v))(e.target.value)} />
                      {/* a card receipt's authorization code - what the bank's settlement report is matched on */}
                      {methodById[g.method_id]?.kind === "card" && (
                        <Input className="w-32 font-mono-fin uppercase" disabled={readOnly} value={r.auth_code || ""} placeholder="Auth code"
                          data-testid={`itemize-auth-${g.attendant_id || "unassigned"}-${g.method_id}-${r.idx}`}
                          onChange={(e) => set((n, v) => (n.payment_transactions[r.idx].auth_code = v))(e.target.value)} />
                      )}
                      <Input className="flex-1" disabled={readOnly} value={r.reference || ""} placeholder={methodById[g.method_id]?.kind === "card" ? "Receipt no." : "Reference / Till code"}
                        onChange={(e) => set((n, v) => (n.payment_transactions[r.idx].reference = v))(e.target.value)} />
                      <Input className="flex-1" disabled={readOnly} value={r.note || ""} placeholder="Note (optional)"
                        onChange={(e) => set((n, v) => (n.payment_transactions[r.idx].note = v))(e.target.value)} />
                      {!readOnly && (
                        <button className="text-rose-500" onClick={() => set((n) => n.payment_transactions.splice(r.idx, 1))(null)} data-testid={`itemize-remove-${g.attendant_id || "unassigned"}-${g.method_id}-${r.idx}`}>×</button>
                      )}
                    </div>
                  ))}
                  {!readOnly && (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => set((n) => n.payment_transactions.push({ attendant_id: g.attendant_id, method_id: g.method_id, amount: "", reference: "", note: "", _k: uid() }))(null)}
                      data-testid={`itemize-add-${g.attendant_id || "unassigned"}-${g.method_id}`}
                    >
                      + Add transaction
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="bg-brand hover:bg-brand/90">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
